import { useState } from "react";
import { SHIP_PARTS, RARITY_COLOR, RARITY_LABEL } from "../data/shipParts";

// ── Ship SVG silhouette ───────────────────────────────────────────────────────
const VB_W = 220;
const VB_H = 260;

function ShipSilhouette({ parts, ownedTiers, equippedTiers, activePart, onPartClick }) {
  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: "100%", height: "100%", overflow: "visible" }}>
      <defs>
        <radialGradient id="shipGlow2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a3a5c" />
          <stop offset="100%" stopColor="#050a14" />
        </radialGradient>
        <radialGradient id="exhaustGlow" cx="50%" cy="0%" r="100%">
          <stop offset="0%" stopColor="#ff8800" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#ff4400" stopOpacity="0" />
        </radialGradient>
        <filter id="dotGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Engine exhaust glow */}
      <ellipse cx="110" cy="238" rx="18" ry="10" fill="url(#exhaustGlow)" />
      <ellipse cx="110" cy="234" rx="10" ry="5" fill="#ffaa33" opacity="0.35" />

      {/* Main hull body */}
      <path d="M110,14 L128,48 L138,118 L132,182 L110,202 L88,182 L82,118 L92,48 Z"
        fill="url(#shipGlow2)" stroke="rgba(100,180,255,0.4)" strokeWidth="1.5" />
      {/* Left wing */}
      <path d="M88,100 L36,148 L52,168 L84,148 Z"
        fill="#0a1a2e" stroke="rgba(100,180,255,0.28)" strokeWidth="1.5" />
      {/* Right wing */}
      <path d="M132,100 L184,148 L168,168 L136,148 Z"
        fill="#0a1a2e" stroke="rgba(100,180,255,0.28)" strokeWidth="1.5" />
      <line x1="56"  y1="150" x2="84"  y2="132" stroke="rgba(0,180,255,0.12)" strokeWidth="1" />
      <line x1="164" y1="150" x2="136" y2="132" stroke="rgba(0,180,255,0.12)" strokeWidth="1" />

      {/* Cockpit */}
      <ellipse cx="110" cy="46" rx="14" ry="18" fill="#0d2a44" stroke="rgba(0,200,255,0.55)" strokeWidth="1.5" />
      <ellipse cx="110" cy="44" rx="9"  ry="13" fill="#1a4a6e" opacity="0.85" />
      <ellipse cx="108" cy="40" rx="3"  ry="4"  fill="rgba(100,220,255,0.3)" />

      {/* Hull stripe */}
      <line x1="110" y1="64" x2="110" y2="188" stroke="rgba(0,200,255,0.12)" strokeWidth="1.5" strokeDasharray="6 5" />

      {/* Engine nozzles */}
      <rect x="94"  y="192" width="14" height="20" rx="3" fill="#0a1520" stroke="rgba(255,100,0,0.45)" strokeWidth="1.5" />
      <rect x="112" y="192" width="14" height="20" rx="3" fill="#0a1520" stroke="rgba(255,100,0,0.45)" strokeWidth="1.5" />
      <ellipse cx="101" cy="212" rx="5" ry="3" fill="#ff6600" opacity="0.4" />
      <ellipse cx="119" cy="212" rx="5" ry="3" fill="#ff6600" opacity="0.4" />

      {/* ── Part hotspots ── */}
      {parts.map(part => {
        const px = (part.position.x / 100) * VB_W;
        const py = (part.position.y / 100) * VB_H;
        const isActive   = activePart === part.id;
        const equippedIdx = equippedTiers[part.id] ?? 0;
        const tier = part.tiers[equippedIdx];
        const rc   = RARITY_COLOR[tier.rarity];
        const dotR = isActive ? 11 : 8;
        const labelRight  = part.position.x < 50;
        const labelX      = labelRight ? px + dotR + 5 : px - dotR - 5;
        const labelAnchor = labelRight ? "start" : "end";

        return (
          <g key={part.id} onClick={() => onPartClick(part.id)} style={{ cursor: "pointer" }}>
            <circle cx={px} cy={py} r={22} fill="transparent" />
            {isActive && (
              <circle cx={px} cy={py} r={18} fill="none" stroke={part.color} strokeWidth="1.5" opacity="0.55" />
            )}
            <line
              x1={labelRight ? px + dotR : px - dotR}
              y1={py}
              x2={labelRight ? px + dotR + 4 : px - dotR - 4}
              y2={py}
              stroke={rc} strokeWidth="1" opacity="0.5"
            />
            <circle
              cx={px} cy={py} r={dotR}
              fill={isActive ? part.color : `${rc}cc`}
              stroke={isActive ? "#ffffff" : part.color}
              strokeWidth={isActive ? 2 : 1.5}
              filter={isActive ? "url(#dotGlow)" : undefined}
            />
            <text x={px} y={py + 4} textAnchor="middle" fontSize={isActive ? 10 : 8}
              fill={isActive ? "#fff" : "rgba(255,255,255,0.8)"}
              style={{ pointerEvents: "none", userSelect: "none" }}
            >{part.icon}</text>
            <text x={labelX} y={py + 4} textAnchor={labelAnchor}
              fontSize="7.5" fontWeight="700"
              fill={isActive ? part.color : "rgba(255,255,255,0.55)"}
              letterSpacing="0.5"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >{part.name.toUpperCase()}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Tier row ──────────────────────────────────────────────────────────────────
function TierRow({ part, tier, tierIdx, isEquipped, isOwned, onEquip }) {
  const rc = RARITY_COLOR[tier.rarity];
  const locked = !isOwned;
  return (
    <button
      onClick={() => !locked && onEquip(part.id, tierIdx)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "8px 10px", borderRadius: 8, fontFamily: "inherit",
        background: isEquipped ? `${rc}18` : locked ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${isEquipped ? rc : locked ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.07)"}`,
        cursor: locked ? "default" : "pointer", textAlign: "left",
        opacity: locked ? 0.35 : 1, transition: "all 0.15s",
      }}
    >
      <span style={{ fontSize: 18, flexShrink: 0, filter: locked ? "grayscale(1)" : "none" }}>{tier.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: locked ? "rgba(255,255,255,0.3)" : rc }}>
            {locked ? "???" : tier.name}
          </span>
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1,
            color: rc, background: `${rc}18`, padding: "1px 4px", borderRadius: 3 }}>
            {RARITY_LABEL[tier.rarity]}
          </span>
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.38)", lineHeight: 1.3 }}>
          {locked ? "Find in mining or combat drops" : tier.desc}
        </div>
        {/* Stat effects */}
        {!locked && Object.keys(tier.effects).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 8px", marginTop: 4 }}>
            {part.stats.map(s => {
              const delta = tier.effects[s.key];
              if (delta == null || delta === 0) return null;
              const sign = typeof delta === "boolean" ? "" : delta > 0 ? "+" : "";
              const val  = typeof delta === "boolean" ? (delta ? "YES" : "NO") : s.fmt(delta);
              return (
                <span key={s.key} style={{ fontSize: 8, color: delta > 0 || delta === true ? "#44ffaa" : "#ff8866" }}>
                  {sign}{val} {s.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {isEquipped && (
        <div style={{ fontSize: 9, fontWeight: 800, color: rc, letterSpacing: 0.5, flexShrink: 0 }}>EQUIPPED</div>
      )}
      {!locked && !isEquipped && (
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", flexShrink: 0 }}>OWNED</div>
      )}
    </button>
  );
}

// ── Part detail panel ─────────────────────────────────────────────────────────
function PartPanel({ part, effectiveStats, ownedTiers, equippedTiers, onEquip, onClose }) {
  if (!part) return null;
  const equippedIdx = equippedTiers[part.id] ?? 0;
  const ownedMax    = ownedTiers[part.id]    ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: `${part.color}18`, border: `1.5px solid ${part.color}55`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
        }}>{part.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: part.color }}>{part.name}</span>
            <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1,
              color: "rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.06)",
              padding: "1px 5px", borderRadius: 3 }}>{part.slot}</span>
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 1 }}>{part.desc}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none",
          color: "rgba(255,255,255,0.3)", fontSize: 16, cursor: "pointer",
          padding: "0 4px", fontFamily: "inherit" }}>✕</button>
      </div>

      {/* Current live stats */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px",
        padding: "7px 10px", background: "rgba(255,255,255,0.03)",
        borderRadius: 7, border: "1px solid rgba(255,255,255,0.06)" }}>
        {part.stats.map(s => {
          const val = effectiveStats[s.key];
          if (val == null) return null;
          return (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>{s.label}</span>
              <span style={{ fontWeight: 700, color: part.color }}>{s.fmt(val)}</span>
            </div>
          );
        })}
      </div>

      {/* Tier list */}
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
        color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>AVAILABLE TIERS</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {part.tiers.map((t, i) => (
          <TierRow
            key={i}
            part={part}
            tier={t}
            tierIdx={i}
            isEquipped={equippedIdx === i}
            isOwned={i <= ownedMax}
            onEquip={onEquip}
          />
        ))}
      </div>

      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", textAlign: "center", marginTop: 2 }}>
        Higher tiers drop from enemies and rare debris sites
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ShipTab({ effectiveStats, ownedTiers, equippedTiers, onEquipTier }) {
  const [activePart, setActivePart] = useState(null);

  const selectedPart = SHIP_PARTS.find(p => p.id === activePart) ?? null;

  function handlePartClick(partId) {
    setActivePart(prev => prev === partId ? null : partId);
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ padding: "7px 14px 6px", flexShrink: 0,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#aaddff" }}>SHIP LOADOUT</div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 1 }}>
            Tap a slot to inspect &amp; equip parts
          </div>
        </div>
        {/* Rarity dots */}
        <div style={{ display: "flex", gap: 6 }}>
          {SHIP_PARTS.map(p => {
            const idx = equippedTiers[p.id] ?? 0;
            const tier = p.tiers[idx];
            const rc   = RARITY_COLOR[tier.rarity];
            return (
              <div key={p.id} title={`${p.name} — ${tier.name}`}
                style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: rc, opacity: activePart === p.id ? 1 : 0.45,
                  boxShadow: activePart === p.id ? `0 0 6px ${rc}` : "none",
                  cursor: "pointer",
                }}
                onClick={() => handlePartClick(p.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Ship silhouette */}
        <div style={{
          flexShrink: 0, height: 320, position: "relative",
          background: "radial-gradient(ellipse at 50% 55%, rgba(0,60,120,0.22) 0%, transparent 70%)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage: "linear-gradient(rgba(0,200,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.035) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }} />
          <div style={{ width: "100%", maxWidth: 340, height: 300, position: "relative", zIndex: 1, padding: "0 10px", boxSizing: "border-box" }}>
            <ShipSilhouette
              parts={SHIP_PARTS}
              ownedTiers={ownedTiers}
              equippedTiers={equippedTiers}
              activePart={activePart}
              onPartClick={handlePartClick}
            />
          </div>
        </div>

        {/* Detail / part list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px 20px" }}>
          {activePart ? (
            <PartPanel
              part={selectedPart}
              effectiveStats={effectiveStats}
              ownedTiers={ownedTiers}
              equippedTiers={equippedTiers}
              onEquip={onEquipTier}
              onClose={() => setActivePart(null)}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
                color: "rgba(255,255,255,0.3)", marginBottom: 3 }}>ALL SLOTS</div>
              {SHIP_PARTS.map(p => {
                const idx  = equippedTiers[p.id] ?? 0;
                const tier = p.tiers[idx];
                const rc   = RARITY_COLOR[tier.rarity];
                const ownedMax = ownedTiers[p.id] ?? 0;
                const bestTier = p.tiers[ownedMax];
                const bestRc   = RARITY_COLOR[bestTier.rarity];
                const hasBetter = ownedMax > idx;
                return (
                  <button key={p.id} onClick={() => handlePartClick(p.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px",
                      background: hasBetter ? "rgba(68,255,170,0.04)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${hasBetter ? "rgba(68,255,170,0.2)" : "rgba(255,255,255,0.07)"}`,
                      borderLeft: `3px solid ${rc}`,
                      borderRadius: "0 8px 8px 0",
                      cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                      transition: "all 0.12s",
                    }}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{p.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{p.name}</span>
                        <span style={{ fontSize: 8, letterSpacing: 0.5, color: "rgba(255,255,255,0.25)" }}>{p.slot}</span>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: rc }}>{tier.name}</span>
                    </div>
                    {hasBetter && (
                      <span style={{ fontSize: 8, fontWeight: 800, color: bestRc,
                        background: `${bestRc}18`, padding: "2px 5px", borderRadius: 4,
                        flexShrink: 0 }}>
                        ↑ {RARITY_LABEL[bestTier.rarity]}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>›</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
